/**
 * Stories API service
 * Handles story metadata fetching from the backend
 */

import { ENV } from '../config/env'
import { authenticatedFetch } from './auth-interceptor'
import type { ApiResponseEnvelope } from './api'
import { ApiError } from './api'
import { transformStoryResponses, type BackendStoryResponse } from './transformers'
import type { PublishedSentenceItemDto, LexemeSummaryDto } from '@/lib/utils/translationApiUtils'
import type { LengthBucketToken } from '@/lib/utils/lengthBucketUtils'

/**
 * Helper to unwrap response envelope: {data, meta} -> data
 */
function unwrapResponse<T>(responseData: ApiResponseEnvelope<T>): T {
    if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        return responseData.data
    }
    return responseData
}

interface ApiErrorResponseBody {
    message?: string
    error?: string
    code?: string
    translation_key?: string
    details?: Record<string, unknown>
    request_id?: string
}

async function handleEnvelope<T>(
    res: Response,
    contextMessage?: string
): Promise<ApiResponseEnvelope<T>> {
    const body = (await res.json().catch(() => ({}))) as
        ApiResponseEnvelope<T> | ApiErrorResponseBody

    if (!res.ok) {
        const errorBody = body as ApiErrorResponseBody
        throw new ApiError(
            errorBody.message ||
                (contextMessage
                    ? `${contextMessage} (${res.status})`
                    : `Request failed (${res.status})`),
            res.status,
            errorBody.error || errorBody.code,
            errorBody.translation_key,
            errorBody.details,
            errorBody.request_id
        )
    }

    return body as ApiResponseEnvelope<T>
}

async function handleResponse<T>(res: Response, contextMessage?: string): Promise<T> {
    return unwrapResponse<T>(await handleEnvelope<T>(res, contextMessage))
}

/**
 * V2 Story title structure
 */
export type StoryTitleDto = {
    canonical: string
    alternates?: string[]
}

/**
 * V2 Story modal (summary) information
 */
export type StoryModalDto = {
    summary: string
    why_chosen?: string[]
    learning_focus?: string
    content_warnings?: string[]
}

/**
 * V2 Story creator attribution
 */
export type StoryCreatorDto = {
    author?: string
    translator?: string
    is_unglossio_original: boolean
}

/**
 * Canonical five-token story length bucket (TRA-125). Storage holds the
 * stable token; display text is a localized, display-only derivation
 * (see lib/utils/lengthBucketUtils, which owns the canonical token tuple).
 */
export type LengthBucket = LengthBucketToken

/**
 * V2 Story DTO. Difficulty (level_cefr) and duration (length_bucket) are
 * flat normalized fields — the sole source of truth; friendly labels are
 * display-only derivations (TRA-73 / TRA-125).
 */
export type StoryDto = {
    story_id: string
    corpus_id: string
    target_lang?: string
    title: StoryTitleDto
    level_cefr: string | null
    length_bucket: LengthBucket
    work_type?: string
    modal: StoryModalDto
    creator: StoryCreatorDto
    attribution?: { text?: string; url?: string }
    rights?: { license?: string; copyright?: string }
    source?: { url?: string; title?: string; publication_date?: string }
    provenance?: { generated_by?: string; generated_at?: string; model_version?: string }
    tags: string[]
}

/**
 * Token analysis from enrichment pipeline
 */
export type TokenAnalysisDto = {
    token: string
    lemma: string
    pos?: string
    features?: Record<string, string>
    start_char?: number
    end_char?: number
}

/**
 * V2 Sentence DTO with seq-based ordering and reference field
 */
export type SentenceDto = {
    id: string
    sentence_id: string
    story_id: string
    story_title: string | null
    seq: number
    text: string
    corpus_id: string
    corpus_string_id: string | null
    reference?: string | null
    token_analyses?: TokenAnalysisDto[]
    enriched_at?: string | null
    text_edited?: boolean
    reference_edited?: boolean
    created_at: string
    updated_at: string
}

export type ConjugationTableDto = {
    label: string
    mood: string
    tense: string
    cells: Record<string, string>
    translation_cells?: Record<string, Record<string, string>>
}

/**
 * V2 Sense definition with translations
 */
export type LexiconSenseDto = {
    id: string
    gloss: string
    translations: {
        en: string[]
        [lang: string]: string[]
    }
}

/**
 * V2 Learning metadata (distractors for quizzes)
 */
export type LexiconLearningDto = {
    distractors: {
        en: string[]
        [lang: string]: string[]
    }
}

/**
 * V2 Lexicon Entry DTO with senses and learning metadata
 */
export type LexiconEntryDto = {
    token: string
    lemma: string
    description?: string
    form_key?: string
    headword?: string
    pos?: string
    senses?: LexiconSenseDto[]
    learning?: LexiconLearningDto
    features?: {
        mood?: string
        tense?: string
        person?: string
        number?: string
        gender?: string
        case?: string
        aspect?: string
        voice?: string
        [key: string]: string | undefined
    }
    forms?: {
        gender?: string
        plural?: string
        /** Backend may return string[] or a plain string depending on LLM output. */
        principal_parts?: string[] | string
    }
    conjugation?: {
        tables?: Record<string, ConjugationTableDto>
    }
    // Legacy field for backward compatibility during migration
    translations?: string[]
}

export type StoryProgressDto = {
    story_id: string
    current_sentence_seq: number
    max_sentence_seq: number
    status: 'not_started' | 'in_progress' | 'completed'
    started_at: string | null
    updated_at: string | null
    completed_at: string | null
    total_sentences: number
    percent_complete: number
}

/**
 * One dashboard recommendation marker entry (TRA-118 bridge stories).
 * Derived live by the backend from completed bridges' outgoing edges.
 */
export type RecommendationMarkerDto = {
    story_id: string
    reasons: string[]
}

/**
 * One completion-triggered recommendation returned by the finish endpoint.
 */
export type CompletionRecommendationDto = {
    story_id: string
    title: string
    level_cefr: string | null
    length_bucket: string | null
    reason: string
    reason_translation_key: string
}

/**
 * Finish-endpoint response: progress plus outgoing-edge recommendations.
 */
export type StoryFinishResultDto = StoryProgressDto & {
    recommendations: CompletionRecommendationDto[]
}

/**
 * V2 User Progress DTO with CEFR-based level
 */
export type UserProgressDto = {
    progress_id: string
    story_id: string
    story_title: string
    corpus_id: string
    target_lang: string
    level_cefr: string | null
    work_type?: string | null
    length_bucket?: string | null
    tags?: string[]
    current_sentence_seq: number
    max_sentence_seq: number
    status: 'in_progress' | 'completed'
    started_at: string | null
    updated_at: string | null
    completed_at: string | null
    total_sentences: number
    percent_complete: number
}

/**
 * Multi-select facet filter params for the library list (TRA-125).
 * Within a facet OR, across facets AND — applied server-side as one
 * indexed Postgres query.
 */
export type StoryFilterParams = {
    target_lang?: string[]
    level_cefr?: string[]
    length_bucket?: string[]
    tags?: string[]
    tag_mode?: 'any' | 'all'
}

/**
 * De-duplicated facet values present in the user's visible library.
 * Served backend-side (GET /stories/facets) so options stay complete
 * once the story list is paginated.
 */
export type StoryFacetsDto = {
    languages: string[]
    level_cefrs: string[]
    length_buckets: string[]
    tags: string[]
}

/**
 * Serialize facet filter params as comma-separated URLSearchParams.
 */
function buildFilterParams(filters?: StoryFilterParams): URLSearchParams {
    const params = new URLSearchParams()
    if (!filters) return params
    for (const key of ['target_lang', 'level_cefr', 'length_bucket', 'tags'] as const) {
        const values = filters[key]
        if (values && values.length > 0) {
            params.set(key, values.join(','))
        }
    }
    if (filters.tag_mode && filters.tag_mode !== 'any') {
        params.set('tag_mode', filters.tag_mode)
    }
    return params
}

/**
 * Serialize facet filter params as a query string (with leading `?`).
 */
function buildFilterQuery(filters?: StoryFilterParams): string {
    const query = buildFilterParams(filters).toString()
    return query ? `?${query}` : ''
}

/**
 * List all stories available to the authenticated user
 * Filtered by user's target language on the backend
 * @param filters - Optional facet filters (within-facet OR, across-facet AND)
 * @returns Array of story metadata (transformed to nested structure)
 * @throws Error if the request fails
 */
export async function listAllStories(filters?: StoryFilterParams): Promise<StoryDto[]> {
    const base = ENV.API_BASE_URL

    const res = await authenticatedFetch(`${base}/api/v1/stories${buildFilterQuery(filters)}`, {
        headers: {
            'Content-Type': 'application/json',
        },
    })

    // Backend returns hybrid flat/nested structure; transform to fully nested
    const backendStories = await handleResponse<BackendStoryResponse[]>(
        res,
        'Failed to fetch stories'
    )
    return transformStoryResponses(backendStories)
}

/**
 * Get de-duplicated facet values for the authenticated user's library.
 * Always spans the full visible library, independent of active filters.
 * @returns Facet values for language, difficulty, length, and tags
 * @throws Error if the request fails
 */
export async function getStoryFacets(): Promise<StoryFacetsDto> {
    const base = ENV.API_BASE_URL

    const res = await authenticatedFetch(`${base}/api/v1/stories/facets`, {
        headers: {
            'Content-Type': 'application/json',
        },
    })

    return handleResponse<StoryFacetsDto>(res, 'Failed to fetch story facets')
}

/**
 * Dashboard library sections served by GET /stories/library. Placement is
 * canonical server-side with precedence completed > in_progress > available.
 */
export type LibrarySection = 'in_progress' | 'completed' | 'available'

/** One fetched library section plus its per-section total (pagination prep). */
export type LibrarySectionResult<T> = {
    items: T[]
    total: number
}

/**
 * Get one dashboard library section with server-side placement + filtering.
 * `available` returns story payloads; `in_progress`/`completed` return
 * progress items (reader-only stories synthesized with a `reader-only:`
 * progress_id marker).
 * @param section - Which placement partition to fetch
 * @param filters - Optional facet filters (within-facet OR, across-facet AND)
 * @throws Error if the request fails
 */
export async function getLibrarySection(
    section: 'available',
    filters?: StoryFilterParams
): Promise<LibrarySectionResult<StoryDto>>
export async function getLibrarySection(
    section: 'in_progress' | 'completed',
    filters?: StoryFilterParams
): Promise<LibrarySectionResult<UserProgressDto>>
export async function getLibrarySection(
    section: LibrarySection,
    filters?: StoryFilterParams
): Promise<LibrarySectionResult<StoryDto> | LibrarySectionResult<UserProgressDto>> {
    const base = ENV.API_BASE_URL
    const params = buildFilterParams(filters)
    params.set('section', section)

    const res = await authenticatedFetch(`${base}/api/v1/stories/library?${params.toString()}`, {
        headers: {
            'Content-Type': 'application/json',
        },
    })

    if (section === 'available') {
        const envelope = await handleEnvelope<BackendStoryResponse[]>(
            res,
            'Failed to fetch library section'
        )
        const backendStories = unwrapResponse(envelope)
        const meta = (envelope as { meta?: { total?: number } }).meta
        return {
            items: transformStoryResponses(backendStories),
            total: meta?.total ?? backendStories.length,
        }
    }

    const envelope = await handleEnvelope<UserProgressDto[]>(res, 'Failed to fetch library section')
    const items = unwrapResponse(envelope)
    const meta = (envelope as { meta?: { total?: number } }).meta
    return { items, total: meta?.total ?? items.length }
}

/**
 * List stories for a given corpus
 * @param corpusId - The corpus ID to fetch stories for
 * @returns Array of story metadata (transformed to nested structure)
 * @throws Error if the request fails
 */
export async function listStories(corpusId: string): Promise<StoryDto[]> {
    const base = ENV.API_BASE_URL
    const res = await authenticatedFetch(
        `${base}/api/v1/corpora/${encodeURIComponent(corpusId)}/stories`,
        {
            headers: { 'Content-Type': 'application/json' },
        }
    )

    // Backend returns hybrid flat/nested structure; transform to fully nested
    const data = await handleResponse<{ stories: BackendStoryResponse[] }>(
        res,
        'Failed to fetch stories'
    )
    return transformStoryResponses(data.stories)
}

/**
 * Response type for story sentences endpoint (v2 with token occurrences).
 */
export type StorySentencesResponse = {
    sentences: PublishedSentenceItemDto[]
    lexemes: Record<string, LexemeSummaryDto>
    target_lang: string
    user_lang: string
    corpus_id: string
    story_id: string
    published_revision: number
}

/**
 * Get all sentences for a story in order
 * @param storyId - The story database ID
 * @returns Array of sentences ordered by their sequence (PublishedSentenceItemDto) with lexemes
 * @throws Error if the request fails
 */
export async function getStorySentences(storyId: string): Promise<StorySentencesResponse> {
    const base = ENV.API_BASE_URL
    const res = await authenticatedFetch(
        `${base}/api/v1/stories/${encodeURIComponent(storyId)}/sentences`,
        {
            headers: { 'Content-Type': 'application/json' },
        }
    )

    const data = await handleResponse<StorySentencesResponse>(res, 'Failed to fetch sentences')
    return data
}

/**
 * Get progress for a specific story
 * @param storyDbId - The story database ID
 * @returns Progress data for the story
 * @throws Error if the request fails
 */
export async function getStoryProgress(storyDbId: string): Promise<StoryProgressDto> {
    const base = ENV.API_BASE_URL

    const res = await authenticatedFetch(
        `${base}/api/v1/stories/${encodeURIComponent(storyDbId)}/progress`,
        {
            headers: {
                'Content-Type': 'application/json',
            },
        }
    )

    return handleResponse<StoryProgressDto>(res, 'Failed to fetch story progress')
}

/**
 * Update progress for a specific story
 * @param storyDbId - The story database ID
 * @param currentSentenceSeq - The current sentence index (0-based)
 * @returns Updated progress data
 * @throws Error if the request fails
 */
export async function updateStoryProgress(
    storyDbId: string,
    currentSentenceSeq: number
): Promise<StoryProgressDto> {
    const base = ENV.API_BASE_URL

    const res = await authenticatedFetch(
        `${base}/api/v1/stories/${encodeURIComponent(storyDbId)}/progress`,
        {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ current_sentence_seq: currentSentenceSeq }),
        }
    )

    return handleResponse<StoryProgressDto>(res, 'Failed to update story progress')
}

/**
 * Get all progress records for the authenticated user
 * @returns Array of user progress records with story metadata
 * @throws Error if the request fails
 */
export async function getUserProgress(): Promise<UserProgressDto[]> {
    const base = ENV.API_BASE_URL

    const res = await authenticatedFetch(`${base}/api/v1/stories/progress`, {
        headers: {
            'Content-Type': 'application/json',
        },
    })

    return handleResponse<UserProgressDto[]>(res, 'Failed to fetch user progress')
}

/**
 * Mark a story as completed for the authenticated user
 * Idempotent operation - can be called multiple times safely
 * @param storyDbId - The story database ID
 * @returns Updated progress data with status 'completed' plus the completed
 *          story's outgoing-edge recommendations (bridge stories)
 * @throws Error if the request fails
 */
export async function finishStory(storyDbId: string): Promise<StoryFinishResultDto> {
    const base = ENV.API_BASE_URL

    const res = await authenticatedFetch(
        `${base}/api/v1/stories/${encodeURIComponent(storyDbId)}/finish`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
        }
    )

    return handleResponse<StoryFinishResultDto>(res, 'Failed to finish story')
}

/**
 * Get the live-derived dashboard recommendation marker map (bridge stories).
 * One entry per story the user is recommended to read next because they
 * completed a bridge that points at it and have not completed it yet.
 * @returns Array of marker entries keyed by target story id
 * @throws Error if the request fails
 */
export async function getStoryRecommendations(): Promise<RecommendationMarkerDto[]> {
    const base = ENV.API_BASE_URL

    const res = await authenticatedFetch(`${base}/api/v1/stories/recommendations`, {
        headers: {
            'Content-Type': 'application/json',
        },
    })

    return handleResponse<RecommendationMarkerDto[]>(res, 'Failed to fetch story recommendations')
}

/**
 * Start a story for the authenticated user, creating a 0% progress record
 * Idempotent operation - can be called multiple times safely
 * @param storyDbId - The story database ID
 * @returns Progress data with status 'in_progress' and 0% completion
 * @throws Error if the request fails
 */
export async function startStory(storyDbId: string): Promise<StoryProgressDto> {
    const base = ENV.API_BASE_URL

    const res = await authenticatedFetch(
        `${base}/api/v1/stories/${encodeURIComponent(storyDbId)}/start`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
        }
    )

    return handleResponse<StoryProgressDto>(res, 'Failed to start story')
}

/**
 * Reset progress for a specific story
 * Clears all progress data, moving the story back to not_started state
 * Idempotent operation - can be called multiple times safely
 * @param storyDbId - The story database ID
 * @returns Initial progress data with status 'not_started'
 * @throws Error if the request fails
 */
export async function resetStoryProgress(storyDbId: string): Promise<StoryProgressDto> {
    const base = ENV.API_BASE_URL

    const res = await authenticatedFetch(
        `${base}/api/v1/stories/${encodeURIComponent(storyDbId)}/reset`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
        }
    )

    return handleResponse<StoryProgressDto>(res, 'Failed to reset story progress')
}
